/**
 * Admin: Vendor Commission Invoices
 * Full admin view with vendor filter, line-item expansion, download, and email actions.
 */
import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../context/AuthContext';
import DashboardLayout from '../components/DashboardLayout';
import { toast } from '../components/ui/sonner';
import {
  Receipt, ChevronDown, ChevronUp, RefreshCw, Loader2,
  AlertCircle, FileText, Send, Search, ExternalLink, CheckCircle2,
  History, PlayCircle, Download, Plus,
} from 'lucide-react';
import { Button }  from '../components/ui/button';
import { Input }   from '../components/ui/input';
import { Badge }   from '../components/ui/badge';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from '../components/ui/dialog';
import { CommissionCreditNotesTab } from '../components/admin/CommissionCreditNotesTab';
import ManualCommissionDocumentModal from '../components/admin/ManualCommissionDocumentModal';
import ExportLedgerButton from '../components/admin/ExportLedgerButton';

// ─── Helpers ──────────────────────────────────────────────────────────────────

function fmt(n) {
  return `₹${Number(n || 0).toLocaleString('en-IN', {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
}
function monthLabel(m) {
  if (!m) return '';
  const [y, mo] = m.split('-');
  return new Date(parseInt(y), parseInt(mo) - 1)
    .toLocaleString('default', { month: 'long', year: 'numeric' });
}

const STATUS_STYLES = {
  finalized: 'bg-blue-100 text-blue-700',
  issued:    'bg-indigo-100 text-indigo-700 font-semibold',
  sent:      'bg-green-100 text-green-700',
  paid:      'bg-emerald-100 text-emerald-800 font-semibold',
  draft:     'bg-gray-100 text-gray-600',
};
function StatusPill({ status }) {
  return (
    <span className={`px-2 py-0.5 rounded-full text-xs ${STATUS_STYLES[status] || 'bg-gray-100 text-gray-500'}`}>
      {status || 'draft'}
    </span>
  );
}

// ─── Line items table ──────────────────────────────────────────────────────────

function LineItemsTable({ items }) {
  return (
    <table className="w-full text-xs mt-2">
      <thead>
        <tr className="bg-gray-50 text-gray-500 text-left">
          <th className="py-2 px-3 font-medium">Order</th>
          <th className="py-2 px-3 font-medium text-right">Sale</th>
          <th className="py-2 px-3 font-medium text-right">Rate</th>
          <th className="py-2 px-3 font-medium text-right">Commission</th>
          <th className="py-2 px-3 font-medium text-right">GST 18%</th>
          <th className="py-2 px-3 font-medium text-right">Total</th>
        </tr>
      </thead>
      <tbody>
        {(items || []).map((li, i) => (
          <tr key={i} className={i % 2 === 0 ? 'bg-white' : 'bg-gray-50/50'}>
            <td className="py-2 px-3 text-gray-700">
              #{li.order_number || (li.order_id || '').slice(0, 8)}
            </td>
            <td className="py-2 px-3 text-right text-gray-600">
              {fmt(li.gross_total)}
              {li.commission_base != null && Math.abs(li.commission_base - li.gross_total) >= 0.01 && (
                <div className="text-[10px] text-amber-600 font-normal" title="Commission is calculated on product value only, excluding shipping/pass-through">
                  on {fmt(li.commission_base)} product value
                </div>
              )}
            </td>
            <td className="py-2 px-3 text-right text-gray-500">{li.commission_rate_pct?.toFixed?.(1) ?? '—'}%</td>
            <td className="py-2 px-3 text-right text-gray-700">{fmt(li.commission)}</td>
            <td className="py-2 px-3 text-right text-amber-700">{fmt(li.commission_gst)}</td>
            <td className="py-2 px-3 text-right font-semibold text-gray-900">{fmt(li.line_total)}</td>
          </tr>
        ))}
      </tbody>
      <tfoot>
        <tr className="border-t border-gray-200">
          <td colSpan={3} className="py-2 px-3 text-xs text-gray-400">SAC: 996211</td>
          <td className="py-2 px-3 text-right font-semibold text-gray-800 text-xs">
            {fmt((items || []).reduce((s, l) => s + (l.commission || 0), 0))}
          </td>
          <td className="py-2 px-3 text-right font-semibold text-amber-700 text-xs">
            {fmt((items || []).reduce((s, l) => s + (l.commission_gst || 0), 0))}
          </td>
          <td className="py-2 px-3 text-right font-bold text-gray-900 text-xs">
            {fmt((items || []).reduce((s, l) => s + (l.line_total || 0), 0))}
          </td>
        </tr>
      </tfoot>
    </table>
  );
}

// ─── Invoice card ─────────────────────────────────────────────────────────────

function InvoiceCard({ inv, onEmail, emailLoading }) {
  const [expanded, setExpanded] = useState(false);
  const [downloading, setDownloading] = useState(null); // null | 'invoice' | 'advice'
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  // iter-303 — was a plain `<a href={apiUrl} target="_blank">`, which
  // navigates with NO Authorization header (browser tab navigation can't
  // carry a Bearer token) — the protected `/html` endpoint always
  // rejected it with 401 "Could not validate credentials". This is the
  // SAME bug already fixed in CommissionInvoicesPage.jsx / pages/vendor/
  // CommissionInvoices.jsx (iter-301f) but this duplicate page
  // (`/vendor-commissions`) was missed. Fetch with the auth header
  // first, then open the fetched HTML as a blob: URL.
  // iter-242 — this button was ALSO mislabeled "Preview / Download" while
  // only ever previewing (opening a blob in a new tab) — never a genuine
  // file download. Split into real separate Preview + Download actions,
  // plus the new Payment Advice / Settlement Note document.
  const previewDoc = async (path, errorMsg) => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    try {
      const response = await fetch(`${BACKEND}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const html = await response.text();
      const blob = new Blob([html], { type: 'text/html' });
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
      setTimeout(() => window.URL.revokeObjectURL(url), 60000);
    } catch (e) {
      toast.error(errorMsg);
    }
  };

  const downloadDoc = async (path, filename, key, errorMsg) => {
    const token = sessionStorage.getItem('token') || localStorage.getItem('token');
    setDownloading(key);
    try {
      const response = await fetch(`${BACKEND}${path}`, {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!response.ok) throw new Error(`HTTP ${response.status}`);
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (e) {
      toast.error(errorMsg);
    } finally {
      setDownloading(null);
    }
  };

  const previewInvoice = () => previewDoc(
    `/api/admin/commission-invoices/${inv.id}/html`, 'Failed to load invoice preview',
  );
  const downloadInvoice = () => downloadDoc(
    `/api/admin/commission-invoices/${inv.id}/pdf`, `${inv.invoice_number || inv.id}.pdf`,
    'invoice', 'Failed to download invoice PDF',
  );
  const previewAdvice = () => previewDoc(
    `/api/admin/commission-invoices/${inv.id}/payment-advice/html`, 'Failed to load payment advice preview',
  );
  const downloadAdvice = () => downloadDoc(
    `/api/admin/commission-invoices/${inv.id}/payment-advice/pdf`,
    `${inv.payment_advice_number || 'payment-advice'}.pdf`, 'advice', 'Failed to download payment advice PDF',
  );

  return (
    <div
      className="border border-gray-200 rounded-xl bg-white overflow-hidden"
      data-testid={`admin-commission-invoice-${inv.id}`}
    >
      {/* Header */}
      <div className="px-5 py-4 flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-2 flex-wrap">
            <span className="font-mono text-sm font-semibold text-gray-900">
              {inv.invoice_number}
            </span>
            <StatusPill status={inv.status} />
            {inv.email_sent_at && (
              <span className="text-xs text-green-600 flex items-center gap-1">
                <CheckCircle2 className="h-3 w-3" /> Emailed
              </span>
            )}
          </div>
          <p className="text-sm font-medium text-gray-700 mt-0.5">
            {inv.vendor_business_name || inv.vendor_name}
          </p>
          <p className="text-xs text-gray-400">
            {inv.vendor_email} · {monthLabel(inv.month)} · {inv.order_count} order{inv.order_count !== 1 ? 's' : ''}
          </p>
        </div>
        <div className="text-right shrink-0">
          <p className="text-xs text-gray-400">Grand Total</p>
          <p className="text-xl font-bold text-gray-900">{fmt(inv.grand_total)}</p>
          <p className="text-xs text-gray-400">
            {fmt(inv.subtotal_commission)} + {fmt(inv.subtotal_gst)} GST
          </p>
        </div>
      </div>

      {/* Payment link */}
      {inv.payment_link_url && (
        <div className="px-5 pb-3 flex items-center gap-2">
          <a
            href={inv.payment_link_url}
            target="_blank"
            rel="noreferrer"
            className="text-xs text-blue-600 hover:text-blue-800 underline flex items-center gap-1"
          >
            <ExternalLink className="h-3 w-3" />
            Payment Link
          </a>
          <span className={`text-xs px-1.5 py-0.5 rounded ${
            inv.payment_link_status === 'paid' ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-500'
          }`}>
            {inv.payment_link_status || 'pending'}
          </span>
        </div>
      )}

      {/* Actions */}
      <div className="px-5 py-3 bg-gray-50 border-t border-gray-100 flex items-center gap-2 flex-wrap">
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7"
          onClick={() => setExpanded(!expanded)}
          data-testid={`toggle-lines-${inv.id}`}
        >
          {expanded ? <ChevronUp className="h-3 w-3 mr-1" /> : <ChevronDown className="h-3 w-3 mr-1" />}
          {expanded ? 'Hide' : 'View'} Line Items ({inv.order_count})
        </Button>

        {/* Preview */}
        <button
          type="button"
          onClick={previewInvoice}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-200 text-xs text-gray-600 hover:bg-gray-100 transition-colors h-7"
          data-testid={`preview-invoice-${inv.id}`}
        >
          <FileText className="h-3 w-3" />
          Preview Invoice
        </button>

        {/* Download */}
        <button
          type="button"
          onClick={downloadInvoice}
          disabled={downloading === 'invoice'}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-gray-200 text-xs text-gray-600 hover:bg-gray-100 transition-colors h-7 disabled:opacity-60"
          data-testid={`download-invoice-${inv.id}`}
        >
          {downloading === 'invoice' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          Download PDF
        </button>

        <div className="w-px h-4 bg-gray-200 mx-1" />

        {/* Payment Advice — Settlement Note (this invoice was deducted from the vendor's payout, not paid separately) */}
        <button
          type="button"
          onClick={previewAdvice}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-emerald-200 text-xs text-emerald-700 hover:bg-emerald-50 transition-colors h-7"
          data-testid={`preview-payment-advice-${inv.id}`}
        >
          <FileText className="h-3 w-3" />
          Preview Payment Advice
        </button>
        <button
          type="button"
          onClick={downloadAdvice}
          disabled={downloading === 'advice'}
          className="inline-flex items-center gap-1 px-3 py-1 rounded-md border border-emerald-200 text-xs text-emerald-700 hover:bg-emerald-50 transition-colors h-7 disabled:opacity-60"
          data-testid={`download-payment-advice-${inv.id}`}
        >
          {downloading === 'advice' ? <Loader2 className="h-3 w-3 animate-spin" /> : <Download className="h-3 w-3" />}
          Download Advice
        </button>

        {/* Email vendor */}
        <Button
          variant="outline"
          size="sm"
          className="text-xs h-7 border-blue-200 text-blue-700 hover:bg-blue-50"
          onClick={() => onEmail(inv.id)}
          disabled={emailLoading === inv.id}
          data-testid={`email-invoice-${inv.id}`}
        >
          <Send className="h-3 w-3 mr-1" />
          {emailLoading === inv.id ? 'Sending…' : (inv.email_sent_at ? 'Resend Email' : 'Email Vendor')}
        </Button>
      </div>

      {/* Line items */}
      {expanded && (
        <div className="border-t border-gray-100 overflow-x-auto">
          <LineItemsTable items={inv.line_items} />
        </div>
      )}
    </div>
  );
}

// ─── Main Page ────────────────────────────────────────────────────────────────

export default function VendorCommissions() {
  const { apiCall } = useAuth();
  const BACKEND = process.env.REACT_APP_BACKEND_URL || '';

  const [invoices,     setInvoices]     = useState([]);
  const [loading,      setLoading]      = useState(false);
  const [error,        setError]        = useState(null);
  const [month,        setMonth]        = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [vendorSearch, setVendorSearch] = useState('');
  const [emailLoading, setEmailLoading] = useState(null); // invoice id being emailed

  // iter-307f — "Sync with Razorpay" / backfill was built into the OTHER
  // admin commission-invoices page (`/admin/commission-invoices` →
  // CommissionInvoicesPage.jsx) but this is a genuinely SEPARATE,
  // duplicate page (`/vendor-commissions`) that never got the same
  // capability. Reuses the exact same 2 backend endpoints already
  // shipped + tested there (iter-307e / iter-305): Step 1 recovers any
  // settled Razorpay transfer missing a local `vendor_payouts` row,
  // Step 2 generates the commission invoice(s) that depend on it.
  const [backfillOpen,    setBackfillOpen]    = useState(false);
  const [backfillMonths,  setBackfillMonths]  = useState(12);
  const [backfillRunning, setBackfillRunning] = useState(false);
  const [backfillPreview, setBackfillPreview] = useState(null);
  const [backfillResult,  setBackfillResult]  = useState(null);
  const [syncPayoutsRunning, setSyncPayoutsRunning] = useState(false);
  const [syncPayoutsPreview, setSyncPayoutsPreview] = useState(null);
  const [syncPayoutsResult,  setSyncPayoutsResult]  = useState(null);
  const [activeTab,       setActiveTab]       = useState('invoices'); // 'invoices' | 'notes'
  const [manualDocOpen,   setManualDocOpen]   = useState(false);
  const [notesReloadKey,  setNotesReloadKey]  = useState(0);

  const loadInvoices = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams({ limit: '500' });
      if (month)        params.set('month',  month);
      if (statusFilter) params.set('status', statusFilter);
      const data = await apiCall(`/api/admin/commission-invoices/?${params}`);
      setInvoices(Array.isArray(data) ? data : []);
    } catch (e) {
      setError(e?.message || 'Failed to load invoices');
    } finally {
      setLoading(false);
    }
  }, [month, statusFilter]);

  useEffect(() => { loadInvoices(); }, [loadInvoices]);

  function resetBackfillDialog() {
    setBackfillOpen(false);
    setBackfillPreview(null);
    setBackfillResult(null);
    setSyncPayoutsPreview(null);
    setSyncPayoutsResult(null);
  }

  async function previewSyncPayouts() {
    setSyncPayoutsRunning(true);
    setSyncPayoutsPreview(null);
    setSyncPayoutsResult(null);
    try {
      const res = await apiCall(
        `/api/admin/commission-invoices/audit/vendor-payouts-backfill?months=${backfillMonths}&dry_run=true`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(res.error || 'Preview failed');
      setSyncPayoutsPreview(res);
    } catch (e) {
      toast.error(e?.message || 'Sync preview failed');
    } finally {
      setSyncPayoutsRunning(false);
    }
  }

  async function confirmSyncPayouts() {
    setSyncPayoutsRunning(true);
    try {
      const res = await apiCall(
        `/api/admin/commission-invoices/audit/vendor-payouts-backfill?months=${backfillMonths}&dry_run=false`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(res.error || 'Sync failed');
      setSyncPayoutsResult(res);
      toast.success(
        res.generated > 0
          ? `Recovered ${res.generated} settled transfer(s) into vendor_payouts.`
          : 'No missing vendor_payouts rows found for settled transfers.'
      );
    } catch (e) {
      toast.error(e?.message || 'Sync failed');
    } finally {
      setSyncPayoutsRunning(false);
    }
  }

  async function previewBackfill() {
    setBackfillRunning(true);
    setBackfillPreview(null);
    setBackfillResult(null);
    try {
      const res = await apiCall(
        `/api/admin/commission-invoices/audit/commission-backfill?months=${backfillMonths}&dry_run=true`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(res.error || 'Preview failed');
      setBackfillPreview(res);
    } catch (e) {
      toast.error(e?.message || 'Backfill preview failed');
    } finally {
      setBackfillRunning(false);
    }
  }

  async function confirmBackfill() {
    setBackfillRunning(true);
    try {
      const res = await apiCall(
        `/api/admin/commission-invoices/audit/commission-backfill?months=${backfillMonths}&dry_run=false`,
        { method: 'POST' }
      );
      if (!res.ok) throw new Error(res.error || 'Backfill failed');
      setBackfillResult(res);
      if (res.generated === 0 && res.failed > 0) {
        toast.error(`Backfill did not generate any invoices — ${res.failed} failed. See details below.`);
      } else if (res.failed > 0) {
        toast.warning(`Backfill partially completed: ${res.generated} generated, ${res.failed} failed.`);
      } else {
        toast.success(`Backfill complete: ${res.generated} invoice(s) generated, ${res.skipped} already existed`);
      }
      loadInvoices();
    } catch (e) {
      toast.error(e?.message || 'Backfill failed');
    } finally {
      setBackfillRunning(false);
    }
  }

  const handleEmail = async (invoiceId) => {
    setEmailLoading(invoiceId);
    try {
      const res = await apiCall(`/api/admin/commission-invoices/${invoiceId}/send-email`, { method: 'POST' });
      // Refresh to show email_sent_at
      loadInvoices();
    } catch (e) {
      alert(e?.message || 'Email failed');
    } finally {
      setEmailLoading(null);
    }
  };

  // Client-side vendor name filter
  const filtered = vendorSearch.trim()
    ? invoices.filter(i => {
        const q = vendorSearch.toLowerCase();
        return (
          (i.vendor_business_name || '').toLowerCase().includes(q) ||
          (i.vendor_name || '').toLowerCase().includes(q) ||
          (i.vendor_email || '').toLowerCase().includes(q)
        );
      })
    : invoices;

  // Summary stats
  const totalCommission = filtered.reduce((s, i) => s + (i.subtotal_commission || 0), 0);
  const totalGst        = filtered.reduce((s, i) => s + (i.subtotal_gst || 0), 0);
  const totalDue        = filtered.reduce((s, i) => s + (i.grand_total || 0), 0);

  return (
    <DashboardLayout>
      <div className="min-h-screen bg-gray-50" data-testid="admin-vendor-commissions-page">

        {/* Page header */}
        <div className="bg-white border-b border-gray-200 px-6 py-5">
          <div className="max-w-5xl mx-auto flex items-center justify-between flex-wrap gap-4">
            <div>
              <h1 className="text-xl font-bold text-gray-900 flex items-center gap-2">
                <Receipt className="h-5 w-5 text-gray-600" />
                Vendor Commission Invoices
              </h1>
              <p className="text-sm text-gray-500 mt-0.5">
                Monthly commission invoices generated for all vendors
              </p>
            </div>
            <div className="flex items-center gap-2">
              <ExportLedgerButton defaultMonth={month} />
              <Button
                size="sm"
                onClick={() => setManualDocOpen(true)}
                data-testid="open-manual-document-btn"
              >
                <Plus className="h-4 w-4 mr-1.5" />
                Create Document
              </Button>
              <Button
                variant="outline"
                size="sm"
                onClick={() => setBackfillOpen(true)}
                data-testid="open-backfill-dialog-btn"
              >
                <History className="h-4 w-4 mr-1.5" />
                Backfill Missing Invoices
              </Button>
              <Button variant="outline" size="sm" onClick={loadInvoices} disabled={loading} data-testid="refresh-btn">
                <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
              </Button>
            </div>
          </div>

          {/* Tabs */}
          <div className="max-w-5xl mx-auto flex items-center gap-1 mt-4" data-testid="vendor-commissions-tabs">
            {[{ id: 'invoices', label: 'Invoices' }, { id: 'notes', label: 'Credit / Debit Notes' }].map(t => (
              <button
                key={t.id}
                onClick={() => setActiveTab(t.id)}
                className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${
                  activeTab === t.id ? 'border-gray-900 text-gray-900' : 'border-transparent text-gray-500 hover:text-gray-700'
                }`}
                data-testid={`vendor-commissions-tab-${t.id}`}
              >
                {t.label}
              </button>
            ))}
          </div>
        </div>

        <div className="max-w-5xl mx-auto px-6 py-6 space-y-5">

          {activeTab === 'notes' ? (
            <CommissionCreditNotesTab reloadSignal={notesReloadKey} />
          ) : (
          <>
          {/* Filters row */}
          <div className="bg-white border border-gray-200 rounded-xl p-4 flex flex-wrap gap-4 items-end">
            <div>
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Month</label>
              <Input
                type="month"
                value={month}
                onChange={e => setMonth(e.target.value)}
                className="h-9 text-sm w-44"
                data-testid="month-filter"
              />
            </div>
            <div className="flex-1 min-w-44">
              <label className="block text-xs text-gray-500 mb-1.5 font-medium">Vendor search</label>
              <div className="relative">
                <Search className="absolute left-2.5 top-2.5 h-3.5 w-3.5 text-gray-400" />
                <Input
                  placeholder="Name or email…"
                  value={vendorSearch}
                  onChange={e => setVendorSearch(e.target.value)}
                  className="h-9 text-sm pl-8"
                  data-testid="vendor-search"
                />
              </div>
            </div>
          </div>

          {/* Status pills */}
          <div className="flex gap-2 flex-wrap">
            {['', 'draft', 'finalized', 'sent', 'paid'].map(s => (
              <button
                key={s}
                onClick={() => setStatusFilter(s)}
                className={`px-3 py-1.5 rounded-full text-xs font-medium border transition-colors ${
                  statusFilter === s
                    ? 'bg-gray-900 text-white border-gray-900'
                    : 'bg-white text-gray-600 border-gray-200 hover:border-gray-400'
                }`}
                data-testid={`status-filter-${s || 'all'}`}
              >
                {s || 'All'}
              </button>
            ))}
          </div>

          {/* Summary stats */}
          {filtered.length > 0 && (
            <div className="grid grid-cols-3 gap-4" data-testid="summary-stats">
              {[
                { label: 'Total Commission',  value: fmt(totalCommission), sub: 'before GST' },
                { label: 'GST @ 18%',         value: fmt(totalGst),        sub: 'SAC 996211'   },
                { label: 'Total Receivable',  value: fmt(totalDue),        sub: `${filtered.length} invoices` },
              ].map(s => (
                <div key={s.label} className="bg-white border border-gray-200 rounded-xl px-4 py-4">
                  <p className="text-xs text-gray-500 font-medium">{s.label}</p>
                  <p className="text-xl font-bold text-gray-900 mt-1">{s.value}</p>
                  <p className="text-xs text-gray-400 mt-0.5">{s.sub}</p>
                </div>
              ))}
            </div>
          )}

          {loading && (
            <div className="flex items-center justify-center py-12">
              <Loader2 className="h-6 w-6 animate-spin text-gray-400" />
            </div>
          )}

          {error && (
            <div className="flex items-center gap-2 text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg p-4" data-testid="error-state">
              <AlertCircle className="h-4 w-4 shrink-0" />
              {error}
            </div>
          )}

          {!loading && !error && filtered.length === 0 && (
            <div className="text-center py-16 text-gray-400" data-testid="empty-state">
              <Receipt className="h-10 w-10 mx-auto mb-3 opacity-30" />
              <p className="text-sm font-medium">
                {month ? `No invoices for ${monthLabel(month)}` : 'No commission invoices generated yet'}
              </p>
              <p className="text-xs mt-1">
                Use "Generate Invoices" from the Commission Invoice admin page.
              </p>
            </div>
          )}

          <div className="space-y-3">
            {filtered.map(inv => (
              <InvoiceCard
                key={inv.id}
                inv={inv}
                onEmail={handleEmail}
                emailLoading={emailLoading}
              />
            ))}
          </div>
          </>
          )}

        </div>
      </div>

      <ManualCommissionDocumentModal
        open={manualDocOpen}
        onOpenChange={setManualDocOpen}
        onCreated={() => { setNotesReloadKey(k => k + 1); loadInvoices(); }}
      />

      {/* Backfill Missing Invoices dialog — Step 1 sync payouts, Step 2 generate invoices */}
      <Dialog open={backfillOpen} onOpenChange={(open) => { if (!open) resetBackfillDialog(); else setBackfillOpen(true); }}>
        <DialogContent className="max-w-2xl" data-testid="backfill-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <History className="h-4 w-4" /> Backfill Missing Commission Invoices
            </DialogTitle>
            <DialogDescription>
              Scans past months of vendor payouts for any vendor that had commission-bearing
              orders but never got a monthly invoice generated. Preview first — nothing is
              written until you confirm.
            </DialogDescription>
          </DialogHeader>

          <div className="border border-blue-200 rounded-lg bg-blue-50/60 p-3 space-y-2" data-testid="sync-payouts-section">
            <p className="text-xs font-semibold text-blue-800 uppercase tracking-wide">
              Step 1 · Sync settled transfers into vendor_payouts (run this first)
            </p>
            <p className="text-xs text-blue-700">
              A settled Razorpay transfer doesn't always leave a local vendor_payouts row (fixed for
              future transfers) — this recovers past ones so Step 2 below can see them.
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                onClick={previewSyncPayouts}
                disabled={syncPayoutsRunning}
                data-testid="sync-payouts-preview-btn"
              >
                {syncPayoutsRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
                Preview
              </Button>
              {syncPayoutsPreview && !syncPayoutsResult && (
                <>
                  <span className="text-xs text-blue-800">
                    {syncPayoutsPreview.generated} missing · {syncPayoutsPreview.skipped} already synced
                  </span>
                  {syncPayoutsPreview.generated > 0 && (
                    <Button
                      size="sm"
                      onClick={confirmSyncPayouts}
                      disabled={syncPayoutsRunning}
                      data-testid="sync-payouts-confirm-btn"
                    >
                      Confirm &amp; Recover {syncPayoutsPreview.generated}
                    </Button>
                  )}
                </>
              )}
              {syncPayoutsResult && (
                <span className="text-xs text-green-700 font-medium" data-testid="sync-payouts-result">
                  Done — {syncPayoutsResult.generated} recovered, {syncPayoutsResult.skipped} already synced,
                  {' '}{syncPayoutsResult.failed} failed.
                </span>
              )}
            </div>
          </div>

          <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide pt-1">
            Step 2 · Generate missing commission invoices
          </p>
          <div className="flex items-center gap-3">
            <label className="text-sm text-gray-600 whitespace-nowrap">Months to scan back:</label>
            <Input
              type="number"
              min={1}
              max={36}
              value={backfillMonths}
              onChange={(e) => setBackfillMonths(Math.max(1, Math.min(36, Number(e.target.value) || 1)))}
              className="w-24"
              data-testid="backfill-months-input"
              disabled={backfillRunning}
            />
            <Button
              size="sm"
              variant="outline"
              onClick={previewBackfill}
              disabled={backfillRunning}
              data-testid="backfill-preview-btn"
            >
              {backfillRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : <PlayCircle className="h-3.5 w-3.5 mr-1.5" />}
              Preview
            </Button>
          </div>

          {backfillPreview && !backfillResult && (
            <div className="border border-gray-200 rounded-lg overflow-hidden" data-testid="backfill-preview-result">
              <div className="px-3 py-2 bg-amber-50 border-b border-amber-100 text-sm text-amber-800 flex items-center justify-between">
                <span>
                  Would generate <strong>{backfillPreview.generated}</strong> invoice(s) ·
                  {' '}{backfillPreview.skipped} already existed
                  {backfillPreview.orphaned_vendor_payouts > 0 && (
                    <> · <span className="text-red-700">{backfillPreview.orphaned_vendor_payouts} orphaned (vendor deleted, skipped)</span></>
                  )}
                </span>
              </div>
              <div className="max-h-56 overflow-y-auto divide-y divide-gray-100">
                {backfillPreview.summary.filter(m => m.vendors_seen > 0).map(m => (
                  <div key={m.month} className="px-3 py-2 text-xs flex items-center justify-between">
                    <span className="font-mono text-gray-600">{monthLabel(m.month)}</span>
                    <span className="text-gray-500">
                      {m.generated} to generate · {m.skipped} existing
                      {m.orphaned_vendor_payouts > 0 && (
                        <span className="text-red-600"> · {m.orphaned_vendor_payouts} orphaned</span>
                      )}
                    </span>
                  </div>
                ))}
                {backfillPreview.summary.every(m => m.vendors_seen === 0) && (
                  <p className="px-3 py-4 text-sm text-gray-400 text-center">
                    Nothing to backfill — every vendor with commission-bearing payouts already
                    has an invoice for every month scanned.
                  </p>
                )}
              </div>
            </div>
          )}

          {backfillResult && (() => {
            const hadFailures = backfillResult.failed > 0;
            const wasNoOp = backfillResult.generated === 0 && hadFailures;
            const tone = wasNoOp
              ? { border: 'border-red-200', bg: 'bg-red-50', text: 'text-red-800', label: 'Backfill did not generate any invoices.' }
              : hadFailures
                ? { border: 'border-amber-200', bg: 'bg-amber-50', text: 'text-amber-800', label: 'Backfill partially completed.' }
                : { border: 'border-green-200', bg: 'bg-green-50', text: 'text-green-800', label: 'Backfill complete.' };
            return (
              <div className={`border ${tone.border} rounded-lg ${tone.bg} px-3 py-3 text-sm ${tone.text}`} data-testid="backfill-confirm-result">
                <p className="font-semibold">{tone.label}</p>
                <p className="mt-1">
                  {backfillResult.generated} invoice(s) generated · {backfillResult.skipped} already existed ·
                  {' '}{backfillResult.failed} failed
                  {backfillResult.orphaned_vendor_payouts > 0 && (
                    <> · {backfillResult.orphaned_vendor_payouts} orphaned vendor_payouts (vendor deleted)</>
                  )}.
                </p>

                {(backfillResult.failures || []).length > 0 && (
                  <div className="mt-3 border-t border-current/20 pt-2 space-y-1.5" data-testid="backfill-failure-details">
                    <p className="text-xs font-semibold uppercase tracking-wide opacity-70">Failure Details</p>
                    {backfillResult.failures.map((f, i) => (
                      <div key={i} className="bg-white/60 rounded px-2.5 py-2 text-xs" data-testid={`backfill-failure-${i}`}>
                        <p className="font-medium">
                          {f.vendor_name || f.vendor_id} {f.vendor_email && <span className="opacity-70">({f.vendor_email})</span>}
                          <span className="opacity-70"> · {f.month}</span>
                        </p>
                        <p className="mt-0.5 font-mono text-[11px] opacity-90">
                          {f.status === 'orphaned_vendor_payouts'
                            ? (f.note || 'Vendor record no longer exists — payout data is orphaned.')
                            : (f.error || 'Unknown error')}
                        </p>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            );
          })()}

          <DialogFooter>
            <Button variant="outline" onClick={resetBackfillDialog} data-testid="backfill-close-btn">
              Close
            </Button>
            {backfillPreview && !backfillResult && backfillPreview.generated > 0 && (
              <Button
                onClick={confirmBackfill}
                disabled={backfillRunning}
                data-testid="backfill-confirm-btn"
              >
                {backfillRunning ? <Loader2 className="h-3.5 w-3.5 mr-1.5 animate-spin" /> : null}
                Confirm &amp; Generate {backfillPreview.generated} Invoice(s)
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </DashboardLayout>
  );
}
